import React, { useEffect, useRef, useState } from 'react';
import Breadcrumb from '../common/breadcrumb/Breadcrumb';
import { useSelector, useDispatch } from 'react-redux';
import { IStore, store } from '../../store';
import styled from 'styled-components';
import {
  setShowLoader,
  setSelectedThreadMessages,
  setUserQuery,
  setShowActivityPanel,
  setProgressCardTitle,
  setActivityPanelActiveTab,
  setFocusedMessage,
  setActivityPanelTitle,
  setShowProgressCard,
  setIsOpenAIEnabled
} from '../../store/actions/deepResearchActions';
import {
  saveUserChatThreadHistory,
  setLastDeletedThreadID,
  toggleLoader
} from '../../store/actions/userChatActions';
import { getStreamResearch, startResearch } from '../../services/deepResearch.service';
import {
  getCurrentSessionId,
  initializeSession,
  setCurrentSessionId
} from '../../services/session.service';
import { getChatThreadConversations, saveUserChatHistory } from '../../services/chatQuery.service';
import {
  handleSessionID,
  handleUserChatThreadHistory,
  setThreadsUnSelected
} from '../../utils/commonUtils';
import LoadingIndicator from '../openAIChat/LoadingIndicator';
import { deepResearchDownArrowScroll } from '../../commonImage';
import { v4 as uuidv4 } from 'uuid';
import ActivityPanel from './ActivityPanel';
import AIContentVerification from './AIContentVerfication';
import DeepResearchInput from './DeepResearchInput';
import ProgressCard from './ProgressCard';
import ThreadView, { ThreadMessage } from './ThreadView';
import { removeQuotes } from './Utils';

const DeepResearch: React.FC = () => {
  const tempIdRef = useRef<string | null>(null);

  //#region Variables
  const breadcrumbs = [{ label: 'Home' }, { label: 'Query with Deep Research', current: true }];
  // Access the chatSearchInput value from the Redux store
  const chatSearchInput = useSelector((state: IStore) => state.common.chatSearchInput);
  const [inputValue, setInputValue] = useState(chatSearchInput ?? '');;
  const [isShowThreadView, setIsShowThreadView] = useState<boolean | null>();
  const [currentThreadMessages, setCurrentThreadMessages] = React.useState<any[]>([]);
  const [isThreadMessagesLoading, setIsThreadMessagesLoading] = useState(false);
  const [firstResponseTime, setFirstResponseTime] = useState(0);
  const [totalResponseTime, setTotalResponseTime] = useState<number | null>();
  //#endregion Variables

  //#region Redux Store
  const dispatch = useDispatch();
  const isRightSideBarOpen: any = useSelector((state: IStore) => state.common.isRightSideBarOpen);
  const userData = useSelector((state: IStore) => state.userProfile?.userData);
  const userChatThreads = useSelector((store: IStore) => store.userChat.chatThreadHistory);
  const [currentUserChatThreads, setCurrentUserChatThreads] = useState(userChatThreads);
  const selectedChatThread = useSelector((store: IStore) => store.userChat.selectedChatThread);
  const lastSavedThreadID = useSelector((state: IStore) => state?.userChat?.lastSavedThreadID);
  const lastDeletedThreadID = useSelector((state: IStore) => state?.userChat?.lastDeletedThreadID);
  const isDeepResearchSelected = useSelector(
    (state: IStore) => state.deepResearch.isDeepResearchEnabled
  );
  const isOpenAIEnabled = useSelector((state: IStore) => state.deepResearch.isOpenAIEnabled);
  const selectedThreadMessages = useSelector(
    (state: IStore) => state.deepResearch.selectedThreadMessages
  );
  const isActivityPanelVisible = useSelector(
    (state: IStore) => state.deepResearch.showActivityPanel
  );
  const isAPILoading = useSelector((state: any) => state.deepResearch.isLoaderEnabled);
  const showProgressCard = useSelector((state: IStore) => state.deepResearch.isProgressCardVisible);

  //#endregion Redux Store

  //#region Effects
  useEffect(() => {
    setCurrentThreadMessages(selectedThreadMessages);
  }, [selectedThreadMessages]);

  useEffect(() => {
    initializeSession();
    resetPreviousState();
  }, []);

  useEffect(() => {
    setCurrentUserChatThreads(userChatThreads);
  }, [userChatThreads]);

  useEffect(() => {
    if (selectedChatThread?.threadId && lastSavedThreadID !== selectedChatThread?.threadId) {
      loadSelectedThreadConversations();
    }
  }, [selectedChatThread]);

  useEffect(() => {
    if (lastDeletedThreadID != 0) {
      const updated = userChatThreads.filter((item: any) => item.threadId !== lastDeletedThreadID);
      setCurrentUserChatThreads(updated);
      dispatch(saveUserChatThreadHistory(updated));
      dispatch(setLastDeletedThreadID(0));
    }
  }, [lastDeletedThreadID]);
  //#endregion Effects

  //#region Helper Functions
  const resetPreviousState = () => {
    setResponseCompleted(false);
    dispatch(setUserQuery(''));
    dispatch(setShowProgressCard(false));
    dispatch(setActivityPanelActiveTab('activity'));
    setFinalChatResponse('');
    pendingSummaryRef.current = false;
    pendingInstructionsRef.current = false;
    pendingSearchWebRef.current = null;
    pendingSearchRef.current = null;
    pendingReasoningRef.current = null;
    pendingCitationRef.current = null;
    pendingFinalChatResponseRef.current = null;
    setIsResponseInProgress(false);
    setSummaryTitle('');
    setInstructionsResponse('');
    // setActivities([]);
    // setCitations([]);
    // setSources([]);
    setResearchCompletionTime('');
    setFirstResponseTime(0);
    setTotalResponseTime(null);
    setCurrentProgress(0);
    setFollowUpSummary('');
  };

  const updateFirstResponseTime = (apiStartTime: number) => {
    if (firstByteTime === null) {
      firstByteTime = performance.now();
      const firstResponseDuration = (firstByteTime - apiStartTime) / 1000;
      setFirstResponseTime(firstResponseDuration);
      return firstResponseDuration; 
    }
    return firstResponseTime; 
  };

  const updateTotalResponseTime = (messageStartTime: number, isStaticResponse: boolean = false) => {
    if (isStaticResponse) {
      setTotalResponseTime(null);
      return null;
    } else {
      const endTime = performance.now();
      const duration = (endTime - messageStartTime) / 1000;
      setTotalResponseTime(duration);
      return duration;
    }
  };

  const getTimeString = (paramStartTime: number) => {
    const endTime = Date.now();
    const diffMs = endTime - paramStartTime;
    if (diffMs === 0) return '0s';
    const minutes = Math.floor(diffMs / (1000 * 60));
    const seconds = Math.floor((diffMs / 1000) % 60);
    return minutes > 0 ? `${minutes}m${seconds > 0 ? ` ${seconds}s` : ''}` : `${seconds}s`;
  };

  const showLoader = () => {
    dispatch(setShowLoader(true));
    dispatch(toggleLoader(true));
  };

  const hideLoader = () => {
    dispatch(setShowLoader(false));
    dispatch(toggleLoader(false));
  };

  const addThreadMessage = (messages: any[]) => {
    dispatch(setFocusedMessage(messages[messages.length - 1]));
    dispatch(setSelectedThreadMessages(messages));
  };
  //#endregion Helper Functions

  //#region API Calls
  const loadSelectedThreadConversations = async () => {
    // resetPreviousState();
    setShowScrollButton(false);
    dispatch(setIsOpenAIEnabled(false));
    dispatch(setShowActivityPanel(false));
    setCurrentThreadMessages([]);
    setInputValue('');
    setIsShowThreadView(true);
    setIsThreadMessagesLoading(true);
    setCurrentSessionId(selectedChatThread?.sessionId);
    let currentThreadConversations = await getChatThreadConversations(selectedChatThread?.threadId);
    if (currentThreadConversations?.messages) {
      setIsThreadMessagesLoading(false);
      setIsShowThreadView(true);
      dispatch(setSelectedThreadMessages(currentThreadConversations.messages));
      dispatch(
        setFocusedMessage(
          currentThreadConversations.messages[currentThreadConversations.messages.length - 1]
        )
      );
    }
  };

  const saveChatConversation = async (
    savedQuery: string,
    receivedMessage: ThreadMessage,
    tempId: string,
    regenerateMessageId?: any,
    messageStartTime?: number,
    calculatedResearchTime?: string,
    calculatedFirstResponseTime?: number
  ) => {
    if (!savedQuery) return;

    const freshTotalResponseTime = messageStartTime
      ? (performance.now() - messageStartTime) / 1000
      : totalResponseTime;

    const freshFirstResponseTime = calculatedFirstResponseTime ?? firstResponseTime;        

    let requestObject: any = {
      threadId: selectedChatThread?.threadId || lastSavedThreadID || 0,
      messageId: regenerateMessageId || 0,
      title: savedQuery,
      dataSource: 9,
      dataSubSource: 'Deep Research',
      sessionId: getCurrentSessionId(),
      question: savedQuery,
      response: JSON.stringify({
        type: receivedMessage?.type,
        activityPanelTitle: receivedMessage?.activityPanelTitle,
        instructions: receivedMessage?.instructions,
        content: receivedMessage?.content,
        activities: receivedMessage?.activities,
        citations: receivedMessage?.citations,
        sources: receivedMessage?.sources,
        researchTime: calculatedResearchTime || researchCompletionTime,
        search_type: isDeepResearchSelected
          ? 'deep-research'
          : isOpenAIEnabled
            ? 'openai-search'
            : '',
        follow_up_summary: followUpSummary
      }),
      firstResponseTime: freshFirstResponseTime,
      totalResponseTime: freshTotalResponseTime
    };
    const response: any = await saveUserChatHistory(requestObject);
    if (response && response?.threadId) {
      if (response.threadId !== selectedChatThread?.threadId) {
        setThreadsUnSelected(currentUserChatThreads);
        handleUserChatThreadHistory(
          requestObject,
          response.threadId,
          currentUserChatThreads,
          userData,
          dispatch
        );
      }
      if (response && response.messageId) {
        // Update the messageId in Redux for the last message
        {
          // Get latest selectedThreadMessages from Redux
          const latestThreadMessages: any = store?.getState
            ? store.getState().deepResearch.selectedThreadMessages
            : selectedThreadMessages;

          const updatedMessages = latestThreadMessages.map((msg: any) =>
            msg.tempId === tempId ? { ...msg, messageId: response.messageId } : msg
          );
          dispatch(setSelectedThreadMessages(updatedMessages));
          dispatch(setFocusedMessage(updatedMessages[updatedMessages.length - 1]));
          
          setSummaryTitle('');
          setInstructionsResponse('');
          setActivityPanelTitle('');
          setCurrentProgress(0);
          setResponseCompleted(false);
          setFirstResponseTime(0);
        }
      }
    }
  };

  const [controller, setController] = useState<any>(null);
  const startTime = performance.now();
  let firstByteTime: any = null;

  const [lastEnteredQuery, setLastEnteredQuery] = useState<string>('');
  const handleSend = async () => {
    // Reset timing metrics for new message
    setFirstResponseTime(0);
    setTotalResponseTime(null);
    firstByteTime = null;

    const userActionStartTime = performance.now();
    messageStartTimeRef.current = userActionStartTime;

    const controllerInstance = new AbortController();
    setController(controllerInstance);

    const trimmed = inputValue.trim();
    if (!trimmed) return;

    setLastEnteredQuery(trimmed);
    setIsShowThreadView(true);
    dispatch(setUserQuery(trimmed));
    setInputValue('');

    const lastMessage = selectedThreadMessages?.length
      ? selectedThreadMessages[selectedThreadMessages.length - 1]
      : null;
    let lastMessageResponse: any = '';
    if (lastMessage && lastMessage.response) {
      try {
        lastMessageResponse = JSON.parse(lastMessage.response);
      } catch {}
    }
    showLoader();
    const tempId = uuidv4();
    tempIdRef.current = tempId;
    handleSessionID(selectedChatThread);
    addThreadMessage([
      ...selectedThreadMessages,
      {
        tempId,
        messageId: 0,
        question: trimmed,
        response: ''
      }
    ]);

    if (
      !lastMessage ||
      (selectedThreadMessages?.length > 1 &&
        isDeepResearchSelected &&
        lastMessageResponse?.type === 'final')
    ) {
      setFinalChatResponse('');
      dispatch(setShowActivityPanel(false));
      dispatch(setShowProgressCard(false));
      dispatch(setActivityPanelTitle(''));
      dispatch(setActivityPanelActiveTab('activity'));
      const result = await startResearch(
        trimmed,
        isDeepResearchSelected,
        selectedChatThread?.threadId
      );
      if (result?.status === 'clarify') {
        const calculatedFirstResponse = updateFirstResponseTime(userActionStartTime);

        updateTotalResponseTime(userActionStartTime);
        if (result?.clarifications) {
          const updatedMessages = [
            ...currentThreadMessages,
            {
              tempId,
              messageId: 0,
              question: trimmed,
              response: JSON.stringify({
                type: result?.status,
                content: result?.clarifications,
                instructions: [],
                activities: [],
                citations: [],
                sources: [],
                researchTime: ''
              })
            }
          ];
          addThreadMessage(updatedMessages);
          saveChatConversation(
            trimmed,
            {
              type: result?.status,
              content: result?.clarifications,
              instructions: result?.instructions,
              activities: result?.activities,
              citations: result?.citations,
              sources: result?.sources,
              researchTime: ''
            },
            tempId,
            undefined,
            userActionStartTime,
            undefined,
            calculatedFirstResponse
          );
        }
        hideLoader();
        firstByteTime = null;
      }
      return;
    }

    if (lastMessageResponse?.type === 'clarify') {
      dispatch(setShowActivityPanel(false));
      dispatch(setShowProgressCard(false));
      dispatch(setActivityPanelTitle(''));
      dispatch(setActivityPanelActiveTab('activity'));
      callStreamingResearch(
        lastMessage,
        lastMessageResponse,
        trimmed,
        controllerInstance,
        userActionStartTime
      );
      return;
    }

    if (lastMessageResponse?.type === 'final' || lastMessageResponse?.type === 'followup') {
      callStreamingResearch(
        lastMessage,
        lastMessageResponse,
        trimmed,
        controllerInstance,
        userActionStartTime
      );
      return;
    }

    const result = await startResearch(trimmed);
    if (result?.status === 'proceed') {
      callStreamingResearch(
        lastMessage,
        lastMessageResponse,
        trimmed,
        controllerInstance,
        userActionStartTime
      );
      hideLoader();
      return;
    }
  };

  const messageStartTimeRef = useRef<number>(0);

  const callStreamingResearch = async (
    lastMessage: any,
    lastMessageResponse: ThreadMessage,
    trimmed: string,
    controllerInstance: any,
    userActionStartTime: number
  ) => {
    try {
      messageStartTimeRef.current = userActionStartTime;
      const startResearchTime = Date.now();
      let response: any;
      if (isDeepResearchSelected) {
        setCurrentProgress(0);
        dispatch(setShowProgressCard(true));
        dispatch(setProgressCardTitle('Defining roles and compiling sources...'));
        dispatch(setActivityPanelTitle(''));
        setActivities([]);
        setCitations([]);
        setSources([]);
        response = await getStreamResearch(
          selectedChatThread?.threadId || lastSavedThreadID,
          lastMessage?.question,
          lastMessageResponse?.content,
          trimmed,
          userData,
          isDeepResearchSelected,
          controllerInstance
        );
      } else if (isOpenAIEnabled) {
        response = await getStreamResearch(
          selectedChatThread?.threadId || lastSavedThreadID,
          trimmed,
          '',
          '',
          userData,
          isDeepResearchSelected,
          controllerInstance
        );
      }

      if (response?.status === 200 && response?.body) {
        if (isDeepResearchSelected) {
          dispatch(setShowActivityPanel(true));
          updateFirstResponseTime(userActionStartTime); // Use streaming API start time
        }
        if (isOpenAIEnabled) {
          setSummaryTitle('');
          setInstructionsResponse('');
          setActivityPanelTitle('');
          setActivities([]);
          setCitations([]);
          dispatch(setShowActivityPanel(false));
          dispatch(setShowProgressCard(false));
          dispatch(setActivityPanelTitle(''));
          dispatch(setActivityPanelActiveTab('activity'));
        }
        const reader = response.body.getReader();
        let chatResponse = '';
        let isCompleted = false;

        const processStream = async () => {
          while (!isCompleted) {
            const { done, value } = await reader.read();
            if (done) break;
            const decoded = new TextDecoder().decode(value);

            let skipNextData = false;

            decoded.split('\n').forEach((line) => {
              if (!line.trim()) return;

              if (isOpenAIEnabled) {
                let response = JSON.parse(line);
                if (response.event === 'follow_up_summary' && response?.answer?.length > 0) {
                  setFinalChatResponse(response?.answer[0]);
                  setIsResponseInProgress(false);
                  isCompleted = true;
                  setResponseCompleted(true);
                  hideLoader();
                  return;
                }
              }

              if (
                line.startsWith('event: other_info') ||
                (line.startsWith('event: follow_up_summary') && isDeepResearchSelected)
              ) {
                skipNextData = true;
                pendingFollowUpSummaryRef.current = true;
                return;
              }

              if (pendingFollowUpSummaryRef.current && skipNextData && line.startsWith('data:')) {
                const dataText = line.replace('data:', '').trim();
                if (dataText) {
                  setFollowUpSummary(dataText);
                  pendingFollowUpSummaryRef.current = false;
                }
                skipNextData = false; // reset flag
                return;
              }

              if (line.startsWith('event: summary')) {
                pendingSummaryRef.current = true;
                return;
              }

              if (pendingSummaryRef.current && line.startsWith('data:')) {
                const dataText = line.replace('data:', '').trim();
                setSummaryTitle(removeQuotes(dataText));
                dispatch(setActivityPanelTitle(removeQuotes(dataText)));
                pendingSummaryRef.current = false;
                return;
              }

              if (line.startsWith('event: instructions')) {
                pendingInstructionsRef.current = true;
                return;
              }
              if (pendingInstructionsRef.current && line.startsWith('data:')) {
                const dataText = line.replace('data:', '');
                if (dataText) {
                  setInstructionsResponse((prev) => prev + dataText);
                  pendingInstructionsRef.current = false;
                  return;
                }
              }

              // Handle event: search_web
              if (line.startsWith('event: search_web')) {
                pendingSearchWebRef.current = {};
                return;
              }

              // Handle event: search
              if (line.startsWith('event: search')) {
                pendingSearchRef.current = '';
                return;
              }

              // Handle event: reasoning
              if (line.startsWith('event: reasoning')) {
                pendingReasoningRef.current = { heading: '', details: '' };
                return;
              }

              // Handle data for search_web URL
              if (pendingSearchWebRef.current !== null && line.startsWith('data:')) {
                const dataText = line.replace('data:', '').trim();
                if (dataText.startsWith('**URL:**')) {
                  const urlMatch = dataText.match(/\[(https?:\/\/[^\]]+)\]/);
                  const url = urlMatch ? urlMatch[1] : '';
                  const domain = url ? new URL(url).hostname.replace('www.', '') : '';
                  setActivities((prev) => [
                    ...prev,
                    {
                      type: 'search_web',
                      url,
                      domain,
                      text: `Read ${domain}`
                    }
                  ]);
                  setSources((prev) => [
                    ...prev,
                    {
                      type: 'search_web',
                      url,
                      domain,
                      text: `Read ${domain}`
                    }
                  ]);
                  pendingSearchWebRef.current = null;
                  return;
                }
              }

              // Handle data for search query
              if (pendingSearchRef.current !== null && line.startsWith('data:')) {
                const dataText = line.replace('data:', '').trim();
                if (dataText.startsWith('Search Query:')) {
                  setActivities((prev) => [
                    ...prev,
                    {
                      type: 'search',
                      text: dataText.replace('Search Query:', 'Searched for').trim()
                    }
                  ]);
                  pendingSearchRef.current = null;
                  return;
                }
              }

              if (pendingReasoningRef.current !== null && line.startsWith('data:')) {
                const dataText = line.replace('data:', '').trim();
                if (dataText.startsWith('**') && dataText.endsWith('**')) {
                  if (pendingReasoningRef.current) {
                    pendingReasoningRef.current.heading = dataText.replace(/\*\*/g, '');
                    dispatch(setProgressCardTitle(pendingReasoningRef.current.heading));
                  }
                  return;
                } else {
                  if (pendingReasoningRef.current && dataText) {
                    pendingReasoningRef.current.details = dataText;
                    setActivities((prev) => [
                      ...prev,
                      {
                        type: 'reasoning',
                        text: dataText // Ensure details is a string
                      }
                    ]);
                    pendingReasoningRef.current = null;
                  }
                  return;
                }
              }

              if (line.startsWith('event: citation')) {
                pendingCitationRef.current = {};
                return;
              }

              // Handle data for citations
              if (pendingCitationRef.current !== null && line.startsWith('data:')) {
                const dataText = line.replace('data:', '').trim();
                // Extract URL from parentheses
                const urlMatch = dataText.match(/\((https?:\/\/[^\s)]+)\)/);
                const url = urlMatch ? urlMatch[1] : '';
                const text = dataText.replace(/\s*\(https?:\/\/[^\s)]+\)/, '').trim();
                setCitations((prev) => [
                  ...prev,
                  {
                    text,
                    url
                  }
                ]);
                pendingCitationRef.current = null;
                return;
              }

              if (line.startsWith('event: message')) {
                chatResponse = '';
                setFinalChatResponse('');
                pendingFinalChatResponseRef.current = true;
                return;
              }

              if (pendingFinalChatResponseRef.current && line.startsWith('data:')) {
                const dataText = line.replace('data:', '');
                if (dataText) {
                  chatResponse += dataText;
                  return;
                }
              }

              if (line.startsWith('event: end')) {
                setFinalChatResponse(chatResponse);
                isCompleted = true;
                pendingFinalChatResponseRef.current = null;
                setIsResponseInProgress(false);
                setResponseCompleted(true);
                updateTotalResponseTime(messageStartTimeRef.current);
                setResearchCompletionTime(getTimeString(startResearchTime));
                dispatch(toggleLoader(false));
                dispatch(setShowProgressCard(false));
                dispatch(setProgressCardTitle('Research Complete'));
              }
            });
          }
        };
        await processStream();
      } else {
        hideLoader();
        dispatch(toggleLoader(false));
        dispatch(setShowProgressCard(false));
      }
    } catch (e) {
      hideLoader();
      if (controller) {
        controller.abort();
      }
    } finally {
      hideLoader();
    }
  };

  const handleRegenerate = (id: string, message: any) => {
    message.isLoading = true;
    handleResend(id, message);
  };

  const handleResend = async (regenerateMessageId: string, message: any) => {
    // Reset timing metrics for regeneration
    setFirstResponseTime(0);
    setTotalResponseTime(null);
    firstByteTime = null;

    const regenerateStartTime = performance.now();

    const controllerInstance = new AbortController();
    setController(controllerInstance);
    dispatch(setFocusedMessage(message));
    const originalQuestion = message.question;
    let lastMessageResponse: any = '';
    if (message && message.response) {
      try {
        lastMessageResponse = JSON.parse(message.response);
      } catch {}
    }

    dispatch(toggleLoader(true));
    handleSessionID(selectedChatThread);

    // Find the index of the message to update
    const msgIndex = selectedThreadMessages.findIndex(
      (msg: any) => msg.messageId === regenerateMessageId || msg.tempId === regenerateMessageId
    );
    if (msgIndex === -1) {
      // hideLoader();
      console.error('Message to regenerate not found');
      return;
    }

    // Find the last clarify message response by searching in reverse order
    let lastClarifyMessage: any = {};
    let lastClarifyMessageResponse: any = '';
    for (let i = msgIndex - 1; i >= 0; i--) {
      const currentMessage = selectedThreadMessages[i];
      if (currentMessage && currentMessage.response) {
        try {
          const parsedResponse = JSON.parse(currentMessage.response);
          if (parsedResponse?.type === 'clarify') {
            lastClarifyMessageResponse = parsedResponse;
            lastClarifyMessage = currentMessage;
            break;
          }
        } catch {
          // Continue searching if parsing fails
          continue;
        }
      }
    }

    const targetMessage = selectedThreadMessages[msgIndex];
    let targetMessageResponse: any = '';
    let regenerateSourceType = '';

    if (targetMessage && targetMessage.response) {
      try {
        targetMessageResponse = JSON.parse(targetMessage.response);
        regenerateSourceType = targetMessageResponse?.search_type || '';
      } catch {}
    }
    const existingMessageId = targetMessage.messageId || regenerateMessageId;

    // Helper to update message in Redux and call save API with existing message ID
    const updateMessage = async (responseObj: any, calculatedResearchTime?: string, calculatedFirstResponseTime?: number) => {
      const updatedMessages = [...selectedThreadMessages];

      const finalResponseObj = {
        ...responseObj,
        researchTime: calculatedResearchTime || researchCompletionTime
      };

      updatedMessages[msgIndex] = {
        ...updatedMessages[msgIndex],
        response: JSON.stringify(finalResponseObj)
      };
      addThreadMessage(updatedMessages);

      await saveChatConversation(
        originalQuestion,
        responseObj,
        targetMessage.tempId || existingMessageId,
        existingMessageId,
        regenerateStartTime,
        calculatedResearchTime,
        calculatedFirstResponseTime
      );
    };

    // Enhanced streaming with proper research mode handling
    const callRegenerateStreaming = async (regenerateSourceType?: string) => {
      try {
        const regenerateStartResearchTime = Date.now();
        setResearchCompletionTime('');
        let response: any;

        let isDRSource =
          regenerateSourceType === 'deep-research' ||
          (regenerateSourceType === '' && isDeepResearchSelected);
        let isOpenAISource =
          regenerateSourceType === 'openai-search' ||
          (regenerateSourceType === '' && isOpenAIEnabled);

        let firstResponseTimeForSave = 0;
        // Default to current mode if no source type from last message
        if (isDRSource) {
          setCurrentProgress(0);
          dispatch(setShowProgressCard(true));
          dispatch(setProgressCardTitle('Defining roles and compiling sources...'));
          dispatch(setActivityPanelTitle(''));
          setActivities([]);
          setCitations([]);
          setSources([]);

          // Use lastClarifyMessageResponse if available for context
          const contextContent =
            lastClarifyMessageResponse?.content || lastMessageResponse?.content;

          response = await getStreamResearch(
            selectedChatThread?.threadId || lastSavedThreadID,
            lastClarifyMessage?.question,
            contextContent,
            originalQuestion,
            userData,
            isDRSource,
            controllerInstance
          );
        } else if (isOpenAISource) {
          response = await getStreamResearch(
            selectedChatThread?.threadId || lastSavedThreadID,
            originalQuestion,
            '',
            '',
            userData,
            isDRSource,
            controllerInstance
          );
        }

        if (response?.status === 200 && response?.body) {
          if (isDRSource) {
            dispatch(setShowActivityPanel(true));
            const calculatedFirstResponse = updateFirstResponseTime(regenerateStartTime); // Use user action time
            firstResponseTimeForSave = calculatedFirstResponse;
          }
          if (isOpenAISource) {
            setSummaryTitle('');
            setInstructionsResponse('');
            setActivityPanelTitle('');
            setActivities([]);
            setCitations([]);
            dispatch(setShowActivityPanel(false));
            dispatch(setShowProgressCard(false));
            dispatch(setActivityPanelTitle(''));
            dispatch(setActivityPanelActiveTab('activity'));
          }

          const reader = response.body.getReader();
          let chatResponse = '';
          let isCompleted = false;

          // Process stream with same logic as callStreamingResearch
          while (!isCompleted) {
            const { done, value } = await reader.read();
            if (done) break;
            const decoded = new TextDecoder().decode(value);
            if (isDRSource) {
              let skipNextData = false;

              decoded.split('\n').forEach(async (line) => {
                if (!line.trim()) return;

                if (
                  line.startsWith('event: other_info') ||
                  (line.startsWith('event: follow_up_summary') && isDeepResearchSelected)
                ) {
                  skipNextData = true;
                  pendingFollowUpSummaryRef.current = true;
                  return;
                }

                if (pendingFollowUpSummaryRef.current && skipNextData && line.startsWith('data:')) {
                  const dataText = line.replace('data:', '').trim();
                  if (dataText) {
                    setFollowUpSummary(dataText);
                    pendingFollowUpSummaryRef.current = false;
                  }
                  skipNextData = false; // reset flag
                  return;
                }

                if (line.startsWith('event: summary')) {
                  pendingSummaryRef.current = true;
                  return;
                }

                if (pendingSummaryRef.current && line.startsWith('data:')) {
                  const dataText = line.replace('data:', '').trim();
                  setSummaryTitle(removeQuotes(dataText));
                  dispatch(setActivityPanelTitle(removeQuotes(dataText)));
                  pendingSummaryRef.current = false;
                  return;
                }

                if (line.startsWith('event: instructions')) {
                  pendingInstructionsRef.current = true;
                  return;
                }
                if (pendingInstructionsRef.current && line.startsWith('data:')) {
                  const dataText = line.replace('data:', '');
                  if (dataText) {
                    setInstructionsResponse((prev) => prev + dataText);
                    pendingInstructionsRef.current = false;
                    return;
                  }
                }

                // Handle event: search_web
                if (line.startsWith('event: search_web')) {
                  pendingSearchWebRef.current = {};
                  return;
                }

                // Handle event: search
                if (line.startsWith('event: search')) {
                  pendingSearchRef.current = '';
                  return;
                }

                // Handle event: reasoning
                if (line.startsWith('event: reasoning')) {
                  pendingReasoningRef.current = { heading: '', details: '' };
                  return;
                }

                // Handle data for search_web URL
                if (pendingSearchWebRef.current !== null && line.startsWith('data:')) {
                  const dataText = line.replace('data:', '').trim();
                  if (dataText.startsWith('**URL:**')) {
                    const urlMatch = dataText.match(/\[(https?:\/\/[^\]]+)\]/);
                    const url = urlMatch ? urlMatch[1] : '';
                    const domain = url ? new URL(url).hostname.replace('www.', '') : '';
                    setActivities((prev) => [
                      ...prev,
                      {
                        type: 'search_web',
                        url,
                        domain,
                        text: `Read ${domain}`
                      }
                    ]);
                    setSources((prev) => [
                      ...prev,
                      {
                        type: 'search_web',
                        url,
                        domain,
                        text: `Read ${domain}`
                      }
                    ]);
                    pendingSearchWebRef.current = null;
                    return;
                  }
                }

                // Handle data for search query
                if (pendingSearchRef.current !== null && line.startsWith('data:')) {
                  const dataText = line.replace('data:', '').trim();
                  if (dataText.startsWith('Search Query:')) {
                    setActivities((prev) => [
                      ...prev,
                      {
                        type: 'search',
                        text: dataText.replace('Search Query:', 'Searched for').trim()
                      }
                    ]);
                    pendingSearchRef.current = null;
                    return;
                  }
                }

                if (pendingReasoningRef.current !== null && line.startsWith('data:')) {
                  const dataText = line.replace('data:', '').trim();
                  if (dataText.startsWith('**') && dataText.endsWith('**')) {
                    if (pendingReasoningRef.current) {
                      pendingReasoningRef.current.heading = dataText.replace(/\*\*/g, '');
                      dispatch(setProgressCardTitle(pendingReasoningRef.current.heading));
                    }
                    return;
                  } else {
                    if (pendingReasoningRef.current && dataText) {
                      pendingReasoningRef.current.details = dataText;
                      setActivities((prev) => [
                        ...prev,
                        {
                          type: 'reasoning',
                          text: dataText // Ensure details is a string
                        }
                      ]);
                      pendingReasoningRef.current = null;
                    }
                    return;
                  }
                }

                if (line.startsWith('event: citation')) {
                  pendingCitationRef.current = {};
                  return;
                }

                // Handle data for citations
                if (pendingCitationRef.current !== null && line.startsWith('data:')) {
                  const dataText = line.replace('data:', '').trim();
                  // Extract URL from parentheses
                  const urlMatch = dataText.match(/\((https?:\/\/[^\s)]+)\)/);
                  const url = urlMatch ? urlMatch[1] : '';
                  const text = dataText.replace(/\s*\(https?:\/\/[^\s)]+\)/, '').trim();
                  setCitations((prev) => [
                    ...prev,
                    {
                      text,
                      url
                    }
                  ]);
                  pendingCitationRef.current = null;
                  return;
                }

                if (line.startsWith('event: message')) {
                  chatResponse = '';
                  setFinalChatResponse('');
                  pendingFinalChatResponseRef.current = true;
                  return;
                }

                if (pendingFinalChatResponseRef.current && line.startsWith('data:')) {
                  const dataText = line.replace('data:', '');
                  if (dataText) {
                    chatResponse += dataText;
                    return;
                  }
                }

                if (line.startsWith('event: end')) {
                  message.isLoading = false;
                  setFinalChatResponse(chatResponse);
                  isCompleted = true;
                  pendingFinalChatResponseRef.current = null;
                  setIsResponseInProgress(false);
                  setResponseCompleted(true);
                  updateTotalResponseTime(regenerateStartTime);
                  const freshResearchTime = getTimeString(regenerateStartResearchTime);
                  setResearchCompletionTime(freshResearchTime);
                  // setResearchCompletionTime(getTimeString(regenerateStartResearchTime));
                  dispatch(toggleLoader(false));
                  dispatch(setShowProgressCard(false));
                  dispatch(setProgressCardTitle('Research Complete'));

                  if (chatResponse) {
                    await updateMessage(
                      {
                        ...lastMessageResponse,
                        content: chatResponse,
                        type: isDRSource ? 'final' : 'followup',
                        researchTime: freshResearchTime
                      },
                      freshResearchTime,
                      firstResponseTimeForSave
                    );
                  }
                }
              });
            } else if (isOpenAISource) {
              decoded.split('\n').forEach((line) => {
                if (!line.trim()) return;

                // Handle message event for final response
                if (line.startsWith('event: message')) {
                  chatResponse = '';
                  return;
                }

                if (line.startsWith('data:')) {
                  const dataText = line.replace('data:', '');
                  if (dataText) {
                    chatResponse += dataText;
                    return;
                  }
                }

                if (line.startsWith('event: end')) {
                  isCompleted = true;
                }
              });
            }
          }
        }
        message.isLoading = false;
      } catch (e) {
        console.error('Regenerate streaming error:', e);
      } finally {
        hideLoader();
        dispatch(setShowProgressCard(false));
      }
    };

    // 1. Clarify scenario
    if (lastMessageResponse?.type === 'clarify') {
      const result = await startResearch(
        originalQuestion,
        isDeepResearchSelected,
        selectedChatThread?.threadId
      );
      if (result?.status === 'clarify' && result?.clarifications) {
        const calculatedFirstResponse = updateFirstResponseTime(regenerateStartTime);

        dispatch(setShowActivityPanel(false));
        dispatch(setShowProgressCard(false));
        dispatch(setActivityPanelTitle(''));
        dispatch(setActivityPanelActiveTab('activity'));
        updateTotalResponseTime(regenerateStartTime);
        message.isLoading = false;
        await updateMessage({
          type: result?.status,
          content: result?.clarifications,
          instructions: result?.instructions || [],
          activities: result?.activities || [],
          citations: result?.citations || [],
          sources: result?.sources || [],
          researchTime: ''
        }, '', calculatedFirstResponse);
      } else if (result?.status === 'proceed') {
        await callRegenerateStreaming();
      }
      hideLoader();
      return;
    }

    // 2. Final/followup scenario - call streaming with proper mode handling
    if (lastMessageResponse?.type === 'final' || lastMessageResponse?.type === 'followup') {
      await callRegenerateStreaming(regenerateSourceType);
      return;
    }

    // 3. Default case - start research and handle based on mode
    const result = await startResearch(
      originalQuestion,
      isDeepResearchSelected,
      selectedChatThread?.threadId
    );
    if (result?.status === 'proceed') {
      await callRegenerateStreaming();
    } else if (result?.status === 'clarify' && result?.clarifications) {
      await updateMessage({
        type: result?.status,
        content: result?.clarifications,
        instructions: result?.instructions || [],
        activities: result?.activities || [],
        citations: result?.citations || [],
        sources: result?.sources || [],
        researchTime: ''
      });
    }

    hideLoader();
  };

  //#endregion API Calls

  //#region Stream Processing
  const focusedMessage = useSelector((state: IStore) => state.deepResearch.focusedMessage);
  const pendingSummaryRef = useRef<boolean>(false);
  const pendingInstructionsRef = useRef<boolean>(false);
  const pendingSearchWebRef = useRef<{ url?: string } | null>(null);
  const pendingSearchRef = useRef<string | null>(null);
  const pendingReasoningRef = useRef<{
    heading?: string;
    details: string;
  } | null>(null);
  const pendingCitationRef = useRef<{ text?: string; url?: string } | null>(null);
  const pendingFinalChatResponseRef = useRef<any>(null);
  const pendingFollowUpSummaryRef = useRef<boolean>(false);
  const [followUpSummary, setFollowUpSummary] = useState<string>('');
  const [isResponseInProgress, setIsResponseInProgress] = useState<boolean>(false);
  const [finalChatResponse, setFinalChatResponse] = useState<string>('');
  const [summaryTitle, setSummaryTitle] = useState<string>(''); // <-- Add this line
  const [instructionsResponse, setInstructionsResponse] = useState<string>(''); // <-- Add this line
  const [activities, setActivities] = useState<any[]>([]);
  const [sources, setSources] = useState<any[]>([]);
  const [citations, setCitations] = useState<any[]>([]);
  const [responseCompleted, setResponseCompleted] = useState(false);
  const [currentProgress, setCurrentProgress] = useState<number>(10);
  const [researchCompletionTime, setResearchCompletionTime] = useState<string>('');
  const isProgressCardVisible = useSelector(
    (state: any) => state.deepResearch.isProgressCardVisible
  );

  useEffect(() => {
    dispatch(setShowActivityPanel(false));
    dispatch(setShowProgressCard(false));
    dispatch(setActivityPanelTitle(''));
    dispatch(setActivityPanelActiveTab('activity'));
    dispatch(setSelectedThreadMessages([]));
  }, []);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;

    if (!isProgressCardVisible) {
      setCurrentProgress(0);
      return;
    }

    if (responseCompleted) {
      setCurrentProgress(100);
      return;
    }

    const MAX_TIME_MS = 35 * 60 * 1000; // 35 mins
    const MIN_COMPLETE_MS = 20 * 60 * 1000; // 20 mins
    const MAX_COMPLETE_MS = 30 * 60 * 1000; // 30 mins

    const INTERVAL_MS = 5000;
    const currentTime = Date.now();

    interval = setInterval(() => {
      if (responseCompleted) {
        setCurrentProgress(100);
        return;
      }

      const elapsed = Date.now() - currentTime;

      setCurrentProgress((prev) => {
        let newProgress = prev;

        if (elapsed <= MIN_COMPLETE_MS / 2) {
          // Phase 1: reach ~50% by 6 mins
          const pct = (elapsed / (MIN_COMPLETE_MS / 2)) * 50;
          newProgress = Math.min(50, Math.max(prev, pct + Math.random() * 2));
        } else if (elapsed <= MAX_COMPLETE_MS) {
          // Phase 2: 50% → 90% between 6–15 mins
          const progressInPhase =
            ((elapsed - MIN_COMPLETE_MS / 2) / (MAX_COMPLETE_MS - MIN_COMPLETE_MS / 2)) * 40; // 40% span
          const pct = 50 + progressInPhase;
          newProgress = Math.min(90, Math.max(prev, pct + Math.random() * 1.5));
        } else if (elapsed <= MAX_TIME_MS) {
          // Phase 3: 90% → 100% between 15–20 mins
          const progressInPhase =
            ((elapsed - MAX_COMPLETE_MS) / (MAX_TIME_MS - MAX_COMPLETE_MS)) * 10; // 10% span
          const pct = 90 + progressInPhase;
          newProgress = Math.min(100, Math.max(prev, pct + Math.random() * 0.3));
        } else {
          // Safety cap
          newProgress = 100;
          if (interval) clearInterval(interval);
        }

        return newProgress;
      });
    }, INTERVAL_MS);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isProgressCardVisible, responseCompleted]);

  useEffect(() => {
    if (responseCompleted) {
      dispatch(setUserQuery(''));
      hideLoader();
      let formattedFocusedMessage = JSON.parse(focusedMessage?.response || '{}');
      if (finalChatResponse?.length > 0) {
        formattedFocusedMessage.content = finalChatResponse;
        formattedFocusedMessage.search_type = isDeepResearchSelected
          ? 'deep-research'
          : isOpenAIEnabled
            ? 'openai-search'
            : '';
        if (isOpenAIEnabled) {
          formattedFocusedMessage.type = 'followup';
        }
      }
      if (tempIdRef.current) {
        const calculatedFirstResponse = firstResponseTime > 0 ? firstResponseTime : 
    (firstByteTime && messageStartTimeRef.current) ? 
    (firstByteTime - messageStartTimeRef.current) / 1000 : 0;

        saveChatConversation(
          lastEnteredQuery,
          formattedFocusedMessage,
          tempIdRef.current,
          undefined,
          messageStartTimeRef.current,
          undefined,
          calculatedFirstResponse
        );
      }
      // resetPreviousState();
      setLastEnteredQuery('');
      setCurrentProgress(100);
      setFollowUpSummary('');
      dispatch(setUserQuery(''));
      dispatch(setShowProgressCard(false));
      //dispatch(setActivityPanelTitle(''));
      dispatch(setActivityPanelActiveTab('activity'));
    }
  }, [responseCompleted]);

  useEffect(() => {
    if (focusedMessage) {
      const updatedActiveMessage = {
        ...focusedMessage,
        response: JSON.stringify({
          type: isDeepResearchSelected ? 'final' : 'followup',
          activityPanelTitle: summaryTitle,
          content: finalChatResponse,
          instructions: instructionsResponse,
          activities: activities,
          citations: citations,
          sources: sources,
          researchTime: researchCompletionTime,
          follow_up_summary: followUpSummary
        })
      };
      dispatch(setFocusedMessage(updatedActiveMessage));
      if (instructionsResponse && instructionsResponse?.length > 0) {
        const updatedMessages = [...currentThreadMessages];
        updatedMessages[updatedMessages.length - 1].response = JSON.stringify({
          type: 'final',
          activityPanelTitle: summaryTitle,
          instructions: instructionsResponse,
          activities: activities,
          citations: citations,
          sources: sources,
          researchTime: researchCompletionTime,
          follow_up_summary: followUpSummary
        });
        dispatch(setSelectedThreadMessages(updatedMessages));
      }
      if (finalChatResponse && finalChatResponse?.length > 0) {
        const updatedMessages = [...currentThreadMessages];
        let prevResponse = {};
        try {
          prevResponse = JSON.parse(updatedMessages[updatedMessages.length - 1].response || '{}');
        } catch {
          prevResponse = {};
        }
        // Merge in the new content
        const newResponse = {
          ...prevResponse,
          type: isDeepResearchSelected ? 'final' : isOpenAIEnabled ? 'followup' : '',
          content: finalChatResponse
        };
        updatedMessages[updatedMessages.length - 1] = {
          ...updatedMessages[updatedMessages.length - 1],
          response: JSON.stringify(newResponse)
        };
        dispatch(setSelectedThreadMessages(updatedMessages));
      }
    }
  }, [instructionsResponse, activities, sources, citations, finalChatResponse]);
  //#endregion Stream Processing

  //#region Activity Panel
  const handleAPIStop = () => {
    if (controller) {
      controller.abort();
    }
  };

  const refToScroll = useRef<HTMLDivElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const handleScrollToBottom = () => {
    setTimeout(() => {
      if (refToScroll && refToScroll.current) {
        refToScroll.current.scrollTo({
          top: refToScroll.current.scrollHeight,
          behavior: 'smooth'
        });
        setShowScrollButton(false);
      }
    }, 0);
  };
  useEffect(() => {
    const scrollContainer = refToScroll.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      // Show button if not at bottom and content is scrollable
      if (scrollHeight > clientHeight && scrollTop + clientHeight < scrollHeight - 10) {
        setShowScrollButton(true);
      } else {
        setShowScrollButton(false);
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    // Initial check
    handleScroll();

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [responseCompleted, finalChatResponse]);
  //#endregion Activity Panel

  //#region Render
  return (
    <StyledLayout>
      <div className='deep-research-main-layout'>
        <div className='deep-research-chatcontainer'>
          <div
            className={
              isRightSideBarOpen
                ? 'deep-research-chatcontainer__breadcrumbOpen'
                : 'deep-research-chatcontainer__breadcrumb'
            }>
            <Breadcrumb items={breadcrumbs} />
          </div>
          <div
            ref={refToScroll}
            className='deep-research-container deep-research-chatcontainer__scroll-container '>
            {!isShowThreadView && (
              <>
                <StyledWelcomeWrapper>
                  <div className='deep-research-header'>
                    <p className='deep-research-title'>Welcome to</p>
                    <p className='deep-research-subtitle'>Deep Research</p>
                  </div>
                </StyledWelcomeWrapper>
                <DeepResearchInput
                  value={inputValue}
                  onChange={setInputValue}
                  onSend={handleSend}
                />
              </>
            )}
            {isShowThreadView && (
              <>
                {isThreadMessagesLoading ? (
                  <StyledPageLoader>
                    <div className='loader-position'>
                      <div className='loader'></div>
                      <p>Loading...</p>
                    </div>
                  </StyledPageLoader>
                ) : (
                  <>
                    <div
                      className={
                        currentThreadMessages?.length === 1
                          ? 'deep-research-chat-container-with-single-message'
                          : ''
                      }>
                      <span>
                        <ThreadView onRegenerate={handleRegenerate} />
                        {showScrollButton && (
                          <div className='deep-research-scroll-button-container'>
                            <button
                              onClick={handleScrollToBottom}
                              className='deep-research-scroll-button'
                              aria-label='Scroll to bottom'>
                              <span>
                                <img src={deepResearchDownArrowScroll} alt='Scroll down' />
                              </span>
                            </button>
                          </div>
                        )}
                      </span>
                      {showProgressCard ? (
                        <ProgressCard progress={currentProgress} handleAPIStop={handleAPIStop} />
                      ) : (
                        <>
                          {isAPILoading ? (
                            <div className='loading-div'>
                              <LoadingIndicator />
                            </div>
                          ) : null}
                        </>
                      )}
                    </div>
                    <div className='thread-view-input-container'>
                      <DeepResearchInput
                        isFromThreadView={true}
                        value={inputValue}
                        onChange={setInputValue}
                        onSend={handleSend}
                      />
                      <div className='ai-generation-suggestion'>
                        <AIContentVerification />
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
        {isActivityPanelVisible && <ActivityPanel />}
      </div>
    </StyledLayout>
  );
  //#endregion Render
};

//#region Styled Components
const StyledLayout = styled.div`
  .loading-div {
    margin: 1rem 0;
  }

  /* Main Chat Container (with or without sidebar) */
  .deep-research-chatcontainer,
  .deep-research-chatcontainer__sidebarClosed {
    position: relative;
    display: flex;
    flex-direction: column;
    // margin: 0px 26px;
    flex: 1 1 0;
    min-width: 0;
    height: 170vh; /* default */

    @media screen and (min-width: 1500px) {
      height: 200vh;
    }

    @media screen and (min-width: 1196px) and (max-width: 1300px) {
      height: 200vh;
    }

    /* Scrollable Section */
    &__scroll-container {
      height: 170vh;
      overflow-y: auto;
      width: 95%;
      padding: 0 2.5rem;
      padding-right: 1.5rem;

      @media screen and (min-width: 1517px) and (max-width: 1900px) {
        height: 90%;
      }

      @media screen and (min-width: 1100px) and (max-width: 1350px) {
        height: 90%;
      }

      /* Scrollbar Styles */
      &::-webkit-scrollbar {
        width: 8px;
      }
      &::-webkit-scrollbar-thumb {
        border-radius: 8px;
        /* background-color: #888; // optional */
      }
    }

    /* Input container */
    &__input-container {
      position: absolute;
      width: 99%;
      bottom: 40px;
    }

    /* Breadcrumbs */
    &__breadcrumbOpen {
      margin-left: 1rem;
      margin-top: 1.5rem;
    }
    &__breadcrumb {
      margin-left: 1rem;
      margin-top: 1.5rem;
    }
  }

  /* Main Layout Flex */
  .deep-research-main-layout {
    margin-left: 3rem;
    display: flex;
    width: 97%;
  }

  /* Titles */
  .deep-research-title {
    font-family: 'ey_interstate';
    font-weight: 300;
    font-size: 18px;
    line-height: 10px;
    color: #1a1a24;
    // padding: 10px 3rem;
    // margin: 0;
  }

  .deep-research-subtitle {
    font-family: 'ey_interstate';
    font-weight: 300;
    font-size: 34px;
    line-height: 42px;
    color: #1275c1;
    // padding: 0px 3rem;
    margin: 0;
  }

  /* Toast container */
  .deep-research-chatcontainer__toast-container-public {
    position: absolute;
    top: -77px;
    width: 30%;
    right: 10%;
    z-index: 9999999999;
  }

  /* Loader */
  .loader-position {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 50vh;
    flex-direction: column;

    .loader {
      border: 5px solid #f3f3f3;
      border-radius: 50%;
      border-top: 5px solid #2e2e38;
      width: 50px;
      height: 50px;
      animation: spin 2s linear infinite;
    }

    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
  }

  // Pill of citation favicons below chat container
  .deep-research-source-download-container {
    display: flex;
    align-items: center;
    vertical-align: middle;
  }
  .deep-research-citation-pill {
    display: flex;
    align-items: center;
    border: 1px solid #4e4e5d;
    border-radius: 32px;
    padding: 6px 18px 6px 10px;
    width: fit-content;
    gap: 10px;
    background: none;
    cursor: pointer;
  }
  .deep-research-citation-pill-icons {
    display: flex;
    align-items: center;
    gap: 0px;
  }
  .deep-research-citation-favicon-img {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    margin-right: -4px;
    border: 1.5px solid #fff;
    background: transparent;
  }
  .deep-research-citation-pill-label {
    color: #000000;
    font-family: 'EYInterstate';
    font-weight: 400;
    font-style: normal;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0;
  }

  .deep-research-scroll-button-container {
    width: '100%';
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 0 12px;
    position: sticky;
    bottom: 2rem;
    z-index: 1000;
    pointer-events: none;
  }

  .deep-research-scroll-button {
    background: none rgba(255, 255, 255, 0.95);
    border: none;
    cursor: pointer;
    width: 32px;
    height: 32px;
    transition: color 0.2s;
    box-shadow: rgba(0, 0, 0, 0.12) 0px 2px 8px;
    border-radius: 50%;
    pointer-events: auto;
  }

  .thread-view-input-container {
    margin-right: 1%;
    margin-top: 2%;
  }
  .deep-research-chat-container-with-single-message {
    min-height: 58vh;
  }
`;

const StyledPageLoader = styled.div`
  margin: auto;
  .loader-position {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 50vh;
    margin: 0;
    text-align: center;
    flex-direction: column;

    .loader {
      border: 5px solid #f3f3f3;
      border-radius: 50%;
      border-top: 5px solid #2e2e38;
      width: 50px;
      height: 50px;
      -webkit-animation: spin 2s linear infinite; /* Safari */
      animation: spin 2s linear infinite;
    }

    /* Safari */
    @-webkit-keyframes spin {
      0% {
        -webkit-transform: rotate(0deg);
      }
      100% {
        -webkit-transform: rotate(360deg);
      }
    }

    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
  }
`;

const StyledWelcomeWrapper = styled.div`
  max-width: 800px;
  // margin: 2rem auto 0 auto;
  // padding: 0px 16px;
  display: flex;
  flex-direction: column;
  align-items: stretch;

  .deep-research-header {
    margin-bottom: 10px;
    text-align: left;
  }
`;

//#endregion Styled Components

export default DeepResearch;
